import type { JSX } from 'react';
import type { DealDetailResource, LostReasonValue, MarkDealLostBody } from '@voxler/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { crmSend, errorMessage, LOST_REASON_LABEL, LOST_REASONS } from '../../lib/crm';
import { MutationError, useCrmRefresh } from './shared';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldError, Fieldset } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';

type FollowUp = 3 | 6 | 12;

const FOLLOW_UPS: readonly { value: FollowUp; label: string }[] = [
  { value: 6, label: 'Dans 6 mois' },
  { value: 3, label: 'Dans 3 mois' },
  { value: 12, label: 'Dans 1 an' },
];

/** « Marquer comme perdue » (LostDialog.dc.html). */
export function LostDialog({
  detail,
  open,
  onClose,
}: {
  detail: DealDetailResource;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? <LostForm detail={detail} onClose={onClose} /> : null}
    </Dialog>
  );
}

function LostForm({
  detail,
  onClose,
}: {
  detail: DealDetailResource;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [reason, setReason] = useState<LostReasonValue | null>(null);
  const [competitor, setCompetitor] = useState('');
  const [comment, setComment] = useState('');
  const [retry, setRetry] = useState(true);
  const [followUp, setFollowUp] = useState<FollowUp>(6);
  const [submitted, setSubmitted] = useState(false);

  const lost = useMutation({
    mutationFn: (chosen: LostReasonValue) => {
      const body: MarkDealLostBody = {
        reason: chosen,
        competitor: chosen === 'competitor' && competitor.trim() !== '' ? competitor.trim() : null,
        comment: comment.trim() === '' ? null : comment.trim(),
        followUpInMonths: retry ? followUp : null,
      };
      return crmSend<DealDetailResource>('POST', `/deals/${detail.deal.id}/lost`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  return (
    <DialogContent>
      <DialogHead
        title="Marquer comme perdue"
        description={`${detail.company.name}, ${detail.deal.title}.`}
      />
      <form
        id="lost-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (reason !== null) {
            lost.mutate(reason);
          }
        }}
      >
        <DialogBody>
          <Fieldset title="Motif *">
            <div role="radiogroup" aria-label="Motif" className="grid gap-0.5">
              {LOST_REASONS.map((value) => (
                <label
                  key={value}
                  className="flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] leading-[18px] hover:bg-accent"
                >
                  <input
                    type="radio"
                    name="lost-reason"
                    className="size-4 accent-primary"
                    checked={reason === value}
                    onChange={() => {
                      setReason(value);
                    }}
                  />
                  {LOST_REASON_LABEL[value]}
                </label>
              ))}
            </div>
            {submitted && reason === null ? <FieldError>Choisir un motif.</FieldError> : null}
            {reason === 'competitor' ? (
              <Field label="Concurrent retenu">
                <Input
                  value={competitor}
                  maxLength={200}
                  placeholder="Nom de l’éditeur ou du prestataire"
                  onChange={(event) => {
                    setCompetitor(event.target.value);
                  }}
                />
              </Field>
            ) : null}
          </Fieldset>

          <Field label="Commentaire">
            <Textarea
              rows={3}
              maxLength={2000}
              placeholder="Ce qui a fait basculer la décision, en une ou deux phrases"
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </Field>

          <Fieldset title="Reprise de contact">
            <CheckRow
              label="Programmer une reprise de contact"
              checked={retry}
              onChange={(event) => {
                setRetry(event.target.checked);
              }}
            />
            {retry ? (
              <Field label="Délai" hideLabel>
                <NativeSelect
                  wrapperClassName="max-w-[220px]"
                  value={String(followUp)}
                  onChange={(event) => {
                    const found = FOLLOW_UPS.find(
                      (option) => String(option.value) === event.target.value,
                    );
                    if (found !== undefined) {
                      setFollowUp(found.value);
                    }
                  }}
                >
                  {FOLLOW_UPS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ) : null}
          </Fieldset>

          <Callout variant="warning">
            Le rappel ouvert de cette affaire sera clôturé. L’entreprise garde tout son historique.
          </Callout>
          <MutationError
            message={lost.isError ? errorMessage(lost.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="lost-form" size="lg" disabled={lost.isPending}>
          Marquer comme perdue
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
